"use client";

import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import AvatarPicker from "@/components/AvatarPicker";
import { LOCAL_STORAGE_AVATAR_KEY, LOCAL_STORAGE_PLAYER_NAME_KEY, LOCAL_STORAGE_LAST_ROOM_KEY, getRandomAvatar, TIMER_PRESETS } from "@/shared/constants";
import { usePublicRooms } from "@/lib/publicRoomsStore";
import type { PublicRoomData } from "@/lib/rtdb";
import { Badge, Card, CardContent, CardHeader, CardTitle, Button, Input } from "@/components/ui";
import { ThemeBackground } from "@/components/ThemeBackground";
import { useTheme } from "@/components/ThemeProvider";

// Lazy-load demo mode so it doesn't bloat the initial home page bundle
const DemoView = lazy(() => import("@/components/demo/DemoView"));

export default function Home() {
  const [playerName, setPlayerName] = useState("");
  const [roomCode, setRoomCode] = useState("");
  const [avatar, setAvatar] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [isPrivateRoom, setIsPrivateRoom] = useState(false);
  const { rooms: publicRooms, loading: loadingRooms } = usePublicRooms();
  const [showDemo, setShowDemo] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const supportRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  // Initialize avatar and player name from localStorage on mount
  useEffect(() => {
    const storedAvatar = localStorage.getItem(LOCAL_STORAGE_AVATAR_KEY);
    setAvatar(storedAvatar || getRandomAvatar());
    const storedName = localStorage.getItem(LOCAL_STORAGE_PLAYER_NAME_KEY);
    if (storedName) setPlayerName(storedName);
  }, []);

  // Close support panel on click outside
  useEffect(() => {
    if (!showSupport) return;
    const handleClick = (e: MouseEvent) => {
      if (supportRef.current && !supportRef.current.contains(e.target as Node)) {
        setShowSupport(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showSupport]);

  const handleAvatarSelect = (newAvatar: string) => {
    setAvatar(newAvatar);
    localStorage.setItem(LOCAL_STORAGE_AVATAR_KEY, newAvatar);
  };

  const handleCreateRoom = () => {
    if (!playerName.trim()) return;
    localStorage.setItem(LOCAL_STORAGE_PLAYER_NAME_KEY, playerName.trim());
    const code = Math.random().toString(36).substring(2, 8).toUpperCase();
    localStorage.setItem(LOCAL_STORAGE_LAST_ROOM_KEY, code);
    const visibility = isPrivateRoom ? "private" : "public";
    router.push(`/room/${code}?name=${encodeURIComponent(playerName)}&create=true&visibility=${visibility}`);
  };

  const handleJoinRoom = () => {
    if (!playerName.trim() || !roomCode.trim()) return;
    localStorage.setItem(LOCAL_STORAGE_PLAYER_NAME_KEY, playerName.trim());
    localStorage.setItem(LOCAL_STORAGE_LAST_ROOM_KEY, roomCode.toUpperCase());
    router.push(`/room/${roomCode.toUpperCase()}?name=${encodeURIComponent(playerName)}`);
  };

  const handleJoinPublicRoom = (code: string) => {
    if (!playerName.trim()) return;
    localStorage.setItem(LOCAL_STORAGE_PLAYER_NAME_KEY, playerName.trim());
    localStorage.setItem(LOCAL_STORAGE_LAST_ROOM_KEY, code);
    router.push(`/room/${code}?name=${encodeURIComponent(playerName)}`);
  };

  const getStatusBadge = (status: PublicRoomData["status"]) => {
    switch (status) {
      case "lobby":
        return <Badge variant="waiting" size="pill">Waiting</Badge>;
      case "playing":
        return <Badge variant="playing" size="pill">In Game</Badge>;
      case "paused":
        return <Badge variant="paused" size="pill">Paused</Badge>;
    }
  };

  const getTimerLabel = (preset: string) => {
    return TIMER_PRESETS[preset as keyof typeof TIMER_PRESETS]?.label || preset;
  };

  return (
    <main className={`relative bg-transparent ${showDemo ? "h-[calc(100vh-3.5rem-1px)] overflow-hidden" : "min-h-[calc(100vh-3.5rem-1px)] flex items-center justify-center px-5 sm:px-6 py-6"}`}>
      {/* Theme-aware Background */}
      <ThemeBackground />

      {showDemo ? (
        /* Demo mode — takes over the full viewport below the header */
        <Suspense
          fallback={
            <div className="flex items-center justify-center h-full">
              <div className="animate-pulse text-muted">Loading demo...</div>
            </div>
          }
        >
          <DemoView
            onClose={() => setShowDemo(false)}
            onCreateRoom={handleCreateRoom}
          />
        </Suspense>
      ) : (
      <div className="w-full max-w-4xl relative z-10">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold mb-2 bg-linear-to-r from-primary to-accent bg-clip-text text-transparent">
            HintGrid
          </h1>
          <p className="text-muted">
            A word guessing party game
          </p>
          <button
            type="button"
            onClick={() => setShowDemo(true)}
            className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            See How It Works
          </button>
        </div>
        {/* Main content grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Left: Public Rooms */}
          <Card variant="elevated" padding="lg">
            <CardHeader>
              <CardTitle>Public Games</CardTitle>
            </CardHeader>
            <CardContent>
              {loadingRooms ? (
                <div className="space-y-3">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="animate-pulse bg-surface rounded-lg h-16 border border-border" />
                  ))}
                </div>
              ) : publicRooms.length > 0 ? (
                <div className="space-y-3" data-testid="public-rooms-list">
                  {publicRooms.map((room) => (
                    <div
                      key={room.roomCode}
                      data-testid={`public-room-${room.roomCode}`}
                      className="p-3 bg-surface rounded-lg border border-border hover:border-accent transition-all duration-300"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-lg text-foreground truncate">
                              {room.roomName}
                            </span>
                            {getStatusBadge(room.status)}
                          </div>
                          <div className="flex items-center gap-3 text-sm text-muted">
                            <span>{room.playerCount} players</span>
                            <span className="text-primary">•</span>
                            <span>{getTimerLabel(room.timerPreset)}</span>
                          </div>
                        </div>
                        <Button
                          onClick={() => {
                            if (playerName.trim()) {
                              router.push(`/room/${room.roomCode}?name=${encodeURIComponent(playerName)}`);
                            } else {
                              router.push(`/room/${room.roomCode}`);
                            }
                          }}
                          data-testid={`public-room-join-${room.roomCode}`}
                          variant="secondary"
                          size="sm"
                        >
                          {room.status === "lobby" ? "Join" : "Watch"}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-4xl mb-3">🎮</div>
                  <p className="text-muted text-lg mb-2">
                    No public games right now
                  </p>
                  <p className="text-sm text-muted/70">
                    Create one and invite friends!
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Right: Create/Join */}
          <Card variant="elevated" padding="lg">
            <CardContent className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
                  Your Name
                </label>
                <div className="flex items-center gap-3">
                  <AvatarPicker selected={avatar} onSelect={handleAvatarSelect} />
                  <Input
                    id="name"
                    data-testid="home-name-input"
                    type="text"
                    value={playerName}
                    onChange={(e) => setPlayerName(e.target.value)}
                    placeholder="Enter your name"
                    className="flex-1"
                    inputSize="lg"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !isCreating) {
                        handleCreateRoom();
                      }
                    }}
                  />
                </div>
              </div>

              <div className="pt-4 border-t border-border space-y-3">
                <Button
                  onClick={handleCreateRoom}
                  disabled={!playerName.trim() || isCreating}
                  data-testid="home-create-btn"
                  variant="primary"
                  size="lg"
                  fullWidth
                >
                  Create {isPrivateRoom ? "Private" : "Public"} Room
                </Button>
                <button
                  type="button"
                  onClick={() => setIsPrivateRoom(!isPrivateRoom)}
                  className="flex items-center justify-between w-full text-sm text-muted"
                >
                  <span>{isPrivateRoom ? "Private room" : "Public room"}</span>
                  <div className={`relative w-11 h-6 rounded-full transition-colors border ${isPrivateRoom ? "bg-primary border-primary" : "bg-muted/20 border-muted/40"}`}>
                    <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-md transition-transform ${isPrivateRoom ? "translate-x-5" : "translate-x-0"}`} />
                  </div>
                </button>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-3 bg-surface-elevated text-muted">or join with code</span>
                </div>
              </div>

              <div>
                <label htmlFor="roomCode" className="block text-sm font-medium text-foreground mb-2">
                  Room Code
                </label>
                <Input
                  id="roomCode"
                  data-testid="home-code-input"
                  type="text"
                  value={roomCode}
                  onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
                  placeholder="ENTER ROOM CODE"
                  className="uppercase tracking-widest"
                  inputSize="lg"
                  maxLength={6}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && playerName.trim() && roomCode.trim()) {
                      handleJoinRoom();
                    }
                  }}
                />
              </div>

              <Button
                onClick={handleJoinRoom}
                disabled={!playerName.trim() || !roomCode.trim()}
                data-testid="home-join-btn"
                variant="secondary"
                size="lg"
                fullWidth
              >
                Join Room
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <footer className="mt-10 text-center text-sm text-muted/70 space-y-3">
          <div ref={supportRef} className="relative inline-block">
            <button
              type="button"
              onClick={() => setShowSupport(!showSupport)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-surface-elevated border border-border text-muted hover:text-primary hover:border-primary/50 transition-colors cursor-pointer"
            >
              <span>&#9749;</span>
              <span>Support HintGrid</span>
            </button>
            {showSupport && (
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 z-50">
                <div className="bg-surface-elevated rounded-xl border border-border shadow-xl overflow-hidden">
                  <div className="flex items-center justify-between px-3 py-2 border-b border-border">
                    <span className="text-xs text-muted">Powered by Ko-fi</span>
                    <button
                      type="button"
                      onClick={() => setShowSupport(false)}
                      className="text-muted hover:text-foreground text-lg leading-none cursor-pointer"
                    >
                      &times;
                    </button>
                  </div>
                  <iframe
                    id="kofiframe"
                    src="https://ko-fi.com/hintgrid/?hidefeed=true&widget=true&embed=true"
                    className="border-0 w-[400px] h-[680px]"
                    title="Support HintGrid on Ko-fi"
                  />
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center justify-center gap-3">
            <Link href="/privacy" className="hover:text-muted transition-colors">
              Privacy Policy
            </Link>
            <span>&middot;</span>
            <Link href="/terms" className="hover:text-muted transition-colors">
              Terms of Service
            </Link>
            <span>&middot;</span>
            <Link href="/credits" className="hover:text-muted transition-colors">
              Credits
            </Link>
          </div>
          <p>&copy; {new Date().getFullYear()} HintGrid. All rights reserved.</p>
        </footer>
      </div>
      )}
    </main>
  );
}


